import React, { Component } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import DoctorSearch from './DoctorSearch';
import SearchSelect from './SearchSelect';
import DoctorPoolsList from './DoctorPoolsList';
import PoolRosterView from './PoolRosterView';
import PoolDoctorsList from './PoolDoctorsList';
import CopyDoctorsPanel from './CopyDoctorsPanel';
import ManagePoolsPanel from './ManagePoolsPanel';
import {
  getAllPools,
  searchPools,
  searchSponsors,
} from '../../services/api/pool-manager';
import { BoldText, RegularText } from '../common/Text';

const OuterComponent = styled.div`
  display: flex;
  flex: 1 0;
  flex-direction: column;
  margin-top: 64px;
  padding: 8px 18px 40px 18px;
  background: #f7f6fb;
  min-height: calc(100vh - 64px);
`;

const Header = styled.div`
  margin: 10px 12px 4px 12px;
`;

const FilterBar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin: 14px 12px;
  padding: 8px;
  background: #ffffff;
  border: 1px solid #eceaf3;
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(75, 46, 131, 0.05);
  width: fit-content;
`;

const Tab = styled.button`
  font-family: BrandonText-Medium, Lato, sans-serif;
  font-size: 0.9rem;
  padding: 9px 20px;
  border: none;
  border-radius: 10px;
  color: ${(props) => (props.active ? '#ffffff' : '#5a5470')};
  background: ${(props) =>
    props.active ? 'linear-gradient(135deg, #6b4bd6, #4b2e83)' : 'transparent'};
  box-shadow: ${(props) =>
    props.active ? '0 6px 14px rgba(75,46,131,0.28)' : 'none'};
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  :hover {
    color: ${(props) => (props.active ? '#ffffff' : '#4b2e83')};
    background: ${(props) =>
      props.active ? 'linear-gradient(135deg, #6b4bd6, #4b2e83)' : '#f4f1fc'};
  }
`;

const PickerRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 12px;
`;

const Columns = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 22rem;
`;

const FullWidth = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Placeholder = styled.div`
  display: flex;
  flex-direction: column;
  margin: 12px;
  padding: 28px;
  align-items: center;
  border: 1px dashed #d8d1ec;
  border-radius: 14px;
  background: #fbfafe;
`;

const MODES = [
  { key: 'doctor', label: 'By Doctor' },
  { key: 'pool', label: 'By Pool' },
  { key: 'sponsor', label: 'By Sponsor' },
  { key: 'copy', label: 'Copy Doctors' },
  { key: 'manage', label: 'Manage Pools' },
];

class DoctorPoolManagerBody extends Component {
  constructor(props) {
    super(props);
    this.state = {
      mode: 'doctor',
      selectedDoctor: null,
      selectedPool: null,
      // Controlled react-select option objects for the By Pool / By Sponsor pickers.
      poolPickerValue: null,
      sponsorPickerValue: null,
      // Full pool list is still needed by the By Doctor tab (DoctorPoolsList add-to-pool).
      pools: [],
      listsLoaded: false,
    };
  }

  componentDidMount() {
    this.loadLists();
  }

  loadLists = () => {
    getAllPools()
      .then((poolsRes) => {
        const pools =
          poolsRes && poolsRes.message === 'success' ? poolsRes.pools || [] : [];
        this.setState({ pools, listsLoaded: true });
      })
      .catch((err) => {
        toast.error(err.message || 'Failed to load pools');
        this.setState({ listsLoaded: true });
      });
  };

  onModeChange = (mode) => {
    this.setState({
      mode,
      selectedDoctor: null,
      selectedPool: null,
      poolPickerValue: null,
      sponsorPickerValue: null,
    });
  };

  onSelectDoctor = (doctor) => {
    this.setState({ selectedDoctor: doctor, selectedPool: null });
  };

  onSelectPoolFromDoctor = (pool) => {
    this.setState({ selectedPool: pool });
  };

  onPickPool = (option) => {
    this.setState({
      poolPickerValue: option || null,
      selectedPool: option
        ? { poolId: option.value, poolName: option.poolName }
        : null,
    });
  };

  onPickSponsor = (option) => {
    this.setState({
      sponsorPickerValue: option || null,
      selectedPool: option
        ? { poolId: option.poolId, poolName: option.poolName }
        : null,
    });
  };

  // Backend pool search (capped at 50 rows server-side). Options carry poolName for downstream use.
  fetchPoolOptions = (input) => {
    const term = (input || '').trim();
    return searchPools(term)
      .then((res) =>
        res && res.message === 'success'
          ? (res.pools || []).map((p) => ({
              value: p.poolId,
              poolName: p.poolName,
              label: `${p.poolName} (${p.poolId})`,
            }))
          : []
      )
      .catch(() => []);
  };

  // Backend sponsor search (capped at 50 rows server-side). Options carry the mapped pool.
  fetchSponsorOptions = (input) => {
    const term = (input || '').trim();
    return searchSponsors(term)
      .then((res) =>
        res && res.message === 'success'
          ? (res.sponsors || []).map((s) => ({
              value: s.sponsorId,
              poolId: s.poolId,
              poolName: s.poolName,
              label: `${s.sponsorName} (${s.sponsorId})`,
            }))
          : []
      )
      .catch(() => []);
  };

  renderDoctorMode() {
    const { selectedDoctor, selectedPool, pools } = this.state;
    return (
      <Columns>
        <Column>
          <DoctorSearch
            selectedDoctorId={selectedDoctor && selectedDoctor.doctorId}
            onSelectDoctor={this.onSelectDoctor}
          />
        </Column>
        <Column>
          {selectedDoctor ? (
            <DoctorPoolsList
              doctorId={selectedDoctor.doctorId}
              doctorName={selectedDoctor.doctorName}
              allPools={pools}
              selectedPoolId={selectedPool && selectedPool.poolId}
              onSelectPool={this.onSelectPoolFromDoctor}
            />
          ) : (
            <Placeholder>
              <RegularText color="#8a84a0">
                Search a doctor to see their pools.
              </RegularText>
            </Placeholder>
          )}
        </Column>
        <Column>
          {selectedDoctor && selectedPool ? (
            <PoolRosterView
              doctorId={selectedDoctor.doctorId}
              poolId={selectedPool.poolId}
              poolName={selectedPool.poolName}
            />
          ) : (
            <Placeholder>
              <RegularText color="#8a84a0">
                Select a pool to manage the roster.
              </RegularText>
            </Placeholder>
          )}
        </Column>
      </Columns>
    );
  }

  renderPoolOrSponsorMode() {
    const { mode, selectedPool, poolPickerValue, sponsorPickerValue } =
      this.state;

    return (
      <React.Fragment>
        <PickerRow>
          {mode === 'pool' ? (
            <SearchSelect
              key="pool-search"
              placeholder="Search a pool by name or ID…"
              loadOptions={this.fetchPoolOptions}
              selected={poolPickerValue}
              onSelect={this.onPickPool}
              width="26rem"
            />
          ) : (
            <SearchSelect
              key="sponsor-search"
              placeholder="Search a sponsor by name or ID…"
              loadOptions={this.fetchSponsorOptions}
              selected={sponsorPickerValue}
              onSelect={this.onPickSponsor}
              width="26rem"
            />
          )}
        </PickerRow>
        {selectedPool ? (
          <FullWidth>
            <PoolDoctorsList
              key={selectedPool.poolId}
              poolId={selectedPool.poolId}
              poolName={selectedPool.poolName}
              wide
              removable
              addable
              rosterOnClick
            />
          </FullWidth>
        ) : (
          <Placeholder>
            <RegularText color="#8a84a0">
              {mode === 'pool'
                ? 'Choose a pool to view its doctors.'
                : 'Choose a sponsor to view its pool doctors.'}
            </RegularText>
          </Placeholder>
        )}
      </React.Fragment>
    );
  }

  renderBody() {
    const { mode } = this.state;
    if (mode === 'doctor') {
      return this.renderDoctorMode();
    }
    if (mode === 'copy') {
      return <CopyDoctorsPanel onCopied={this.loadLists} />;
    }
    if (mode === 'manage') {
      return <ManagePoolsPanel onChanged={this.loadLists} />;
    }
    return this.renderPoolOrSponsorMode();
  }

  render() {
    const { mode } = this.state;

    return (
      <OuterComponent>
        <Header>
          <BoldText fontSize="1.5rem">Pool Manager</BoldText>
          <RegularText color="#8a84a0">
            Manage pool rosters, doctor status, and pools.
          </RegularText>
        </Header>
        <FilterBar>
          {MODES.map((m) => (
            <Tab
              key={m.key}
              active={mode === m.key}
              onClick={() => this.onModeChange(m.key)}
            >
              {m.label}
            </Tab>
          ))}
        </FilterBar>
        {this.renderBody()}
      </OuterComponent>
    );
  }
}

export default DoctorPoolManagerBody;
